import { useEffect, useState } from 'react';

/**
 * Shared presentation rules for the listing / checkout / store location maps
 * (RAS-2473, RAS-2474). The search map is deliberately not a consumer of this.
 */

// Mapbox serves 512px tiles, so metres-per-pixel is 78271.5 * cos(lat) / 2^zoom.
// At zoom 13 the 1000m privacy circle is ~290px across, which overflows these
// map frames and is cropped at the edges. Zoom 14 is too tight: the circle
// swallows the whole frame and reads as a flat tint with no visible arc.
export const PRIVACY_CIRCLE_ZOOM = 13;

// Street-level zoom for the opted-in exact-address view. Without fuzzy the
// shared fallback in Map.js is zoom 11, far too coarse to read as "precise".
export const EXACT_LOCATION_ZOOM = 16;

const PRIVACY_CIRCLE_RADIUS = 1000; // metres

/**
 * Builds the maps config for a location map.
 *
 * Disabling fuzzy is the single switch that swaps both the static and the
 * dynamic map from the radius circle over to a precise marker.
 */
export const buildLocationMapsConfig = (baseMapsConfig, showExactLocation) => ({
  ...baseMapsConfig,
  fuzzy: {
    enabled: !showExactLocation,
    offset: PRIVACY_CIRCLE_RADIUS,
    defaultZoomLevel: PRIVACY_CIRCLE_ZOOM,
    circleColor: '#F2704E',
    fillOpacity: 0.15,
    strokeOpacity: 1.0,
  },
});

/**
 * An owner only gets the exact-address treatment when they opted in *and* the
 * address actually exists. Fail safe to the privacy circle otherwise, so we
 * never pair a precise pin with a blank address row.
 */
export const shouldShowExactLocation = (isAddressPublic, address) =>
  isAddressPublic === true && typeof address === 'string' && address.trim().length > 0;

/**
 * Pulls a displayable one-line address out of one address-ish object.
 *
 * Store addresses are saved by StoreBasicInformationPage.duck.js as
 * { address, city, state, postcode, country, origin } where `address` is already the
 * full formatted line from the location autocomplete. `formattedAddress` is accepted
 * first because some older records carry it, and the city/state/postcode/country
 * compose is a last resort for records saved without the formatted line at all.
 */
const pickAddressLine = source => {
  if (!source) return '';
  if (typeof source === 'string') return source.trim();
  if (typeof source !== 'object') return '';

  const formatted =
    typeof source.formattedAddress === 'string' ? source.formattedAddress.trim() : '';
  if (formatted) return formatted;

  const line = typeof source.address === 'string' ? source.address.trim() : '';
  if (line) return line;

  return [source.city, source.state, source.postcode, source.country]
    .map(part => (typeof part === 'string' ? part.trim() : ''))
    .filter(Boolean)
    .join(', ');
};

/**
 * Resolves the address to show for a store, from the owner's profile publicData.
 *
 * Order matches what an owner would expect from Store Settings:
 *   1. an explicitly filled store address,
 *   2. otherwise the personal/profile address - which covers BOTH "store address is the
 *      same as my personal address" (storeAddressSameAsPersonal, where no separate
 *      storeAddress record is written) and stores that only ever filled in a profile
 *      address.
 *
 * Whether the result is actually rendered is a separate decision - see
 * shouldShowExactLocation and the owner's showAddressInStore opt-in.
 */
export const resolveStoreAddress = (publicData = {}) => {
  const pd = publicData || {};
  return pickAddressLine(pd.storeAddress) || pickAddressLine(pd);
};

export const isMapboxReady = () =>
  typeof window !== 'undefined' && !!window.mapboxgl && !!window.mapboxgl.accessToken;

/**
 * Mapbox GL is loaded with `defer`, so it is usually not ready on first paint.
 * Map.js renders an empty div and never retries when the library is missing, so
 * these maps render the static image first and upgrade to the interactive map
 * (which is what exposes the +/- zoom controls) once this fires.
 *
 * Returns an unsubscribe function.
 */
export const onMapboxReady = callback => {
  if (typeof window === 'undefined') {
    return () => {};
  }
  if (isMapboxReady()) {
    callback();
    return () => {};
  }
  window.addEventListener('mapboxgl:ready', callback);
  return () => window.removeEventListener('mapboxgl:ready', callback);
};

/**
 * Hook flavour of onMapboxReady. Starts false so the first client render matches
 * the server render, then upgrades from the effect.
 */
export const useMapboxReady = () => {
  const [isReady, setIsReady] = useState(false);

  useEffect(() => onMapboxReady(() => setIsReady(true)), []);

  return isReady;
};
